import type { OpencodeToolPart } from "schemas/generated/agentTools/opencode";

import {
  mcpDownloadAssetsInputSchema,
  mcpUploadAssetsInputSchema,
} from "schemas/agentTools/replo/bedrockMcp";
import { z } from "zod";

import { quotedText } from "../shared";
import {
  getInputDetailPart,
  getToolPartOutputRecord,
  naturalDetail,
} from "./bedrock-shared";

export function getAssetFindDetail({
  toolPart,
}: {
  toolPart: OpencodeToolPart;
}) {
  const outputRecord = getToolPartOutputRecord({ toolPart });
  const assets = Array.isArray(outputRecord?.assets)
    ? outputRecord.assets
    : null;
  if (assets && assets.length > 0) {
    return `with ${assets.length === 1 ? "1 result" : `${assets.length} results`}`;
  }
  return naturalDetail({
    parts: [
      getInputDetailPart({ label: "type", properties: ["type"], toolPart }),
      getInputDetailPart({ label: "tags", properties: ["tags"], toolPart }),
    ],
  });
}

const assetInputDetailSchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    url: z.string().trim().min(1).optional(),
    r2Uri: z.string().trim().min(1).optional(),
    source: z
      .object({
        url: z.string().trim().min(1).optional(),
      })
      .partial()
      .optional(),
  })
  .partial();

const assetJobStatusInputSchema = z.object({
  jobIds: z.array(z.string()),
});

function getAssetInputDetail({ asset }: { asset: unknown }) {
  const parsedAsset = assetInputDetailSchema.safeParse(asset);
  if (!parsedAsset.success) {
    return null;
  }

  const detail =
    parsedAsset.data.name ??
    parsedAsset.data.url ??
    parsedAsset.data.r2Uri ??
    parsedAsset.data.source?.url;
  return detail ? quotedText({ value: detail }) : null;
}

export function getAssetJobStatusDetail({
  toolPart,
}: {
  sourceToolName: "download_assets" | "upload_assets";
  toolPart: OpencodeToolPart;
  toolParts: readonly OpencodeToolPart[];
}) {
  const parsedInput = assetJobStatusInputSchema.safeParse(toolPart.state.input);
  const jobCount = parsedInput.success ? parsedInput.data.jobIds.length : 0;
  return jobCount > 0
    ? `for ${jobCount === 1 ? "1 job" : `${jobCount} jobs`}`
    : null;
}

export function getDownloadAssetsDetail({
  toolPart,
}: {
  toolPart: OpencodeToolPart;
}) {
  const parsedInput = mcpDownloadAssetsInputSchema.safeParse(
    toolPart.state.input,
  );
  const assets = parsedInput.success ? parsedInput.data.assets : [];
  const firstAsset = assets.flatMap((asset) => {
    const detail = getAssetInputDetail({ asset });
    return detail ? [detail] : [];
  })[0];

  if (!firstAsset) {
    return null;
  }

  return assets.length > 1
    ? `${firstAsset} and ${assets.length - 1} more`
    : firstAsset;
}

export function getUploadAssetsDetail({
  toolPart,
}: {
  toolPart: OpencodeToolPart;
}) {
  const parsedInput = mcpUploadAssetsInputSchema.safeParse(
    toolPart.state.input,
  );
  const assets = parsedInput.success ? parsedInput.data.assets : [];
  const assetDetails = assets.flatMap((asset) => {
    const detail = getAssetInputDetail({ asset });
    return detail ? [detail] : [];
  });

  return assetDetails.length > 0 ? assetDetails.join(", ") : null;
}
